import { LocationOn, MyLocationOutlined } from "@mui/icons-material";
import { Autocomplete, Box, Grid, TextField, Typography } from "@mui/material";
import { useLocationWeatherContext } from "../contexts/LocationWeatherContext";
import { useDeferredValue, useEffect, useState } from "react";
import { DEFAULT_DEVICE_LOCATION, GeoLocationByPlace } from "../types/GeoLocation";
import { getGeoLocationByPlace } from "../services/weatherAndLocationService";


const LocationSearchTextBox = () => {
  const [inputValue, setInputValue] = useState('');
  const { setCurrentSearchLocation, geocodingBaseUrl, lastSearchedLocation } = useLocationWeatherContext();

  const deferredInputValue = useDeferredValue(inputValue);
  const [geoLocations, setGeoLocations] = useState<GeoLocationByPlace[]>();
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    setIsPending(true);
    getGeoLocationByPlace(deferredInputValue, geocodingBaseUrl)
      .then((geoLocations) => {
        setGeoLocations(geoLocations);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsPending(false);
      });
  }, [deferredInputValue, geocodingBaseUrl]);

  const getRegionInfo = (geoLocation: GeoLocationByPlace): string => {
    if (!geoLocation) {
      return "";
    }
    let regionInfo = [];
    if (geoLocation.admin1) regionInfo.push(geoLocation.admin1);
    if (geoLocation.admin2) regionInfo.push(geoLocation.admin2);
    if (geoLocation.admin3) regionInfo.push(geoLocation.admin3);
    if (geoLocation.admin4) regionInfo.push(geoLocation.admin4);
    if (geoLocation.country) regionInfo.push(geoLocation.country);
    return regionInfo.join(", ");
  }

  if (!lastSearchedLocation) {
    return <></>;
  }

  return (
    <Box sx={{ pt: 2, pb: 2, pl: 4, pr: 4 }}>
      <Autocomplete
        id="search-location-textbox"
        fullWidth
        getOptionLabel={(option: GeoLocationByPlace) => option.name}
        filterOptions={(x) => x}
        options={geoLocations && geoLocations.length > 0 ? [DEFAULT_DEVICE_LOCATION, ...geoLocations] : [DEFAULT_DEVICE_LOCATION]}
        autoComplete
        includeInputInList
        isOptionEqualToValue={(option, value) => true}
        value={lastSearchedLocation}
        noOptionsText="No locations"
        onChange={(event: any, newValue: GeoLocationByPlace | null) => {
          if (newValue && newValue.id === DEFAULT_DEVICE_LOCATION.id) {
            // console.log("User selected Your Current Location");
            setCurrentSearchLocation(DEFAULT_DEVICE_LOCATION);
          }
          else if (newValue) { // Using a custom location the user selected
            // console.log("User selected a place:", newValue.name);
            setCurrentSearchLocation(newValue);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if (newInputValue !== DEFAULT_DEVICE_LOCATION.name) {
            // console.log("User entered a place:", newInputValue);
            setInputValue(newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Search places" fullWidth />
        )}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  {
                    option.isDeviceLocation ?
                      <MyLocationOutlined sx={{ color: 'text.secondary' }} /> :
                      <LocationOn sx={{ color: 'text.secondary' }} />
                  }
                </Grid>
                <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  <Box
                    key={option.id}
                    component="span"
                    sx={{ fontWeight: 'regular' }}
                  >
                    {option.name}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {getRegionInfo(option)}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </Box>
  );
}

export default LocationSearchTextBox;